import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Server, 
  Sparkles,
  Globe,
  Shield,
  Mail,
  CheckCircle,
  ExternalLink,
  Copy,
  Check
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { 
  useUserCloud, 
  EmailCaptureModal,
  PromptBlock,
  EmergentCTA, 
  AuthorCard,
  resourceColors as colors 
} from '../ResourceComponents';

const resource = {
  id: 'backend-setup',
  type: 'guide',
  platform: 'Website',
  title: 'Backend & Domain Setup Guide',
  description: 'Step-by-step instructions to connect your domain and get your website live.',
  color: '#00CED1',
  author: {
    name: 'techbaenae',
    handle: '@techbaenae',
    avatar: 'https://customer-assets.emergentagent.com/job_e02576fc-3e7a-4446-bc21-d558624baf1e/artifacts/zhpzfvij_naomi%20headshot%20zoomed%20in.jpg',
    title: 'Data Dreamer'
  },
};

// Checklist item component
const ChecklistItem = ({ children, checked }) => (
  <div className="flex items-start gap-3 py-2">
    <div className={`w-5 h-5 rounded border flex-shrink-0 mt-0.5 flex items-center justify-center ${checked ? 'bg-teal-500 border-teal-500' : 'border-gray-300'}`}>
      {checked && <Check className="w-3 h-3 text-white" />}
    </div>
    <span className="text-gray-700">{children}</span>
  </div>
);

// Step card component
const StepCard = ({ number, title, children, color = '#00CED1' }) => (
  <div className="relative pl-12 pb-8 border-l-2 border-gray-200 last:border-l-0 last:pb-0">
    <div 
      className="absolute left-0 top-0 w-8 h-8 -translate-x-[17px] rounded-full flex items-center justify-center text-white font-bold text-sm"
      style={{ background: color }}
    >
      {number}
    </div>
    <div className="bg-gray-50 rounded-xl p-5">
      <h4 className="font-semibold text-gray-900 mb-3">{title}</h4>
      <div className="text-sm text-gray-600 space-y-2">
        {children}
      </div>
    </div>
  </div>
);

// Domain registrar option
const RegistrarOption = ({ name, description, pros, link, recommended }) => (
  <a 
    href={link}
    target="_blank"
    rel="noopener noreferrer"
    className={`block p-5 rounded-xl border-2 transition-all hover:shadow-md ${recommended ? 'border-teal-500 bg-teal-50/50' : 'border-gray-200 hover:border-gray-300'}`}
  >
    <div className="flex items-start justify-between mb-2">
      <h4 className="font-semibold text-gray-900">{name}</h4>
      {recommended && (
        <span className="px-2 py-0.5 rounded-full bg-teal-500 text-white text-xs font-medium">
          Recommended
        </span>
      )}
    </div>
    <p className="text-sm text-gray-600 mb-3">{description}</p>
    <ul className="space-y-1">
      {pros.map((pro, i) => (
        <li key={i} className="flex items-center gap-2 text-xs text-gray-500">
          <CheckCircle className="w-3 h-3 text-teal-500" />
          {pro}
        </li>
      ))}
    </ul>
  </a>
);

const BackendSetupPage = () => {
  const { userEmail, saveUser, saveResource } = useUserCloud();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    if (userEmail) {
      setHasAccess(true);
      saveResource(resource.id);
    }
  }, [userEmail]);

  const handleSaveToCloud = async (email) => {
    saveUser(email);
    saveResource(resource.id);
    setShowEmailModal(false);
    setHasAccess(true);
  };

  if (!hasAccess && !userEmail) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="max-w-lg w-full text-center">
          <div 
            className="w-24 h-24 rounded-3xl mx-auto mb-6 flex items-center justify-center"
            style={{ background: `${resource.color}20` }}
          >
            <Server className="w-12 h-12" style={{ color: resource.color }} />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{resource.title}</h1>
          <p className="text-gray-600 mb-8">{resource.description}</p>
          
          <button
            onClick={() => setShowEmailModal(true)}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-medium text-white transition-all hover:opacity-90"
            style={{ background: colors.teal }}
          >
            <Sparkles className="w-5 h-5" />
            Save to Your Cloud
          </button>
          
          <p className="text-gray-400 text-sm mt-4">
            Enter your email to access this resource
          </p>

          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mt-8 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Data Dreaming
          </Link>
        </div>

        <EmailCaptureModal
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          onSubmit={handleSaveToCloud}
          resourceTitle={resource.title}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/"
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </Link>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Resource by</span>
            <span className="text-sm font-medium text-gray-900">{resource.author.handle}</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div 
        className="py-16 px-4"
        style={{ background: `linear-gradient(180deg, ${resource.color}10 0%, transparent 100%)` }}
      >
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <span 
              className="px-3 py-1 rounded-full text-xs font-medium"
              style={{ background: `${resource.color}20`, color: resource.color }}
            >
              Setup Guide
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{resource.title}</h1>
          <p className="text-xl text-gray-600 max-w-2xl">{resource.description}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        
        {/* Before You Start Checklist */}
        <div className="mb-12 p-6 rounded-2xl border-2" style={{ borderColor: resource.color, background: `${resource.color}08` }}>
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" style={{ color: resource.color }} />
            Before You Start Checklist
          </h3>
          <div className="grid md:grid-cols-2 gap-x-8">
            <ChecklistItem>Choose your domain name (yourname.com, yourbrand.com)</ChecklistItem>
            <ChecklistItem>Pick a domain registrar (see options below)</ChecklistItem>
            <ChecklistItem>Have a profile photo ready (square, high quality)</ChecklistItem>
            <ChecklistItem>List your social links (Instagram, LinkedIn, etc.)</ChecklistItem>
            <ChecklistItem>Write your bio/tagline (keep it short)</ChecklistItem>
          </div>
        </div>

        {/* Domain Registrars */}
        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Globe className="w-6 h-6" style={{ color: resource.color }} />
          Step 1: Get Your Domain
        </h3>
        
        <div className="grid md:grid-cols-3 gap-4 mb-12">
          <RegistrarOption 
            name="Cloudflare"
            description="Fast DNS, great security, at-cost pricing."
            pros={['Fastest DNS', 'No markup on domains', 'Free SSL']}
            link="https://cloudflare.com"
            recommended
          />
          <RegistrarOption 
            name="Namecheap"
            description="Affordable with good customer support."
            pros={['Low prices', 'Free WhoisGuard', 'Easy interface']}
            link="https://namecheap.com"
          />
          <RegistrarOption 
            name="GoDaddy"
            description="Most popular registrar, beginner-friendly."
            pros={['Simple setup', 'Phone support', 'Lots of extensions']}
            link="https://godaddy.com"
          />
        </div>

        {/* Step by step DNS */}
        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Shield className="w-6 h-6" style={{ color: resource.color }} />
          Step 2: Connect Your Domain
        </h3>

        <div className="mb-12 space-y-4">
          <StepCard number="1" title="Purchase Your Domain">
            <p>Go to your chosen registrar and search for your desired domain. Add it to cart and complete checkout. Most .com domains cost $10-15/year.</p>
          </StepCard>
          
          <StepCard number="2" title="Find DNS Settings">
            <p>After purchase, look for "DNS Management" or "DNS Settings" in your dashboard:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li><strong>GoDaddy:</strong> My Products → DNS</li>
              <li><strong>Namecheap:</strong> Domain List → Manage → Advanced DNS</li>
              <li><strong>Cloudflare:</strong> Select domain → DNS</li>
            </ul>
          </StepCard>
          
          <StepCard number="3" title="Add DNS Records">
            <p>Your hosting platform (like Emergent) will provide specific DNS records. Typically you'll add:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li><strong>A Record:</strong> Points your domain to the server IP</li>
              <li><strong>CNAME Record:</strong> Points www to your main domain</li>
            </ul>
          </StepCard>
          
          <StepCard number="4" title="Wait for Propagation">
            <p>DNS changes take 15 minutes to 48 hours to propagate worldwide. Usually it's under 1 hour. You can check status at <a href="https://dnschecker.org" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">dnschecker.org</a></p>
          </StepCard>
        </div>

        <PromptBlock
          title="Help Me Connect My Domain"
          description="Use this prompt to get step-by-step help for your specific registrar."
          prompt={`I purchased a domain from [GODADDY/NAMECHEAP/CLOUDFLARE].

Please help me:
1. What DNS records do I need to add?
2. Step-by-step instructions for my specific registrar
3. How long until it's live?
4. How to set up HTTPS/SSL`}
        />

        <EmergentCTA />

        {/* Email Setup */}
        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Mail className="w-6 h-6" style={{ color: resource.color }} />
          Step 3: Set Up Professional Email (Optional)
        </h3>

        <div className="mb-8 p-6 rounded-2xl bg-gray-50">
          <p className="text-gray-600 mb-4">
            Want an email like <span className="font-mono bg-white px-2 py-0.5 rounded border">hello@yourdomain.com</span>? Here are your options:
          </p>
          
          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 rounded-xl bg-white border border-gray-200">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 font-bold">G</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Google Workspace</h4>
                <p className="text-sm text-gray-600">~$6/month. Full Gmail experience with your domain.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-4 rounded-xl bg-white border border-gray-200">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center flex-shrink-0">
                <span className="text-yellow-600 font-bold">Z</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Zoho Mail</h4>
                <p className="text-sm text-gray-600">Free tier available. Great for starting out.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-4 rounded-xl bg-white border border-gray-200">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                <span className="text-gray-600 font-bold">→</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Email Forwarding</h4>
                <p className="text-sm text-gray-600">Free with most registrars. Forwards to your Gmail.</p>
              </div>
            </div>
          </div>
        </div>

        <PromptBlock
          title="Set Up Email on My Domain"
          description="Get help setting up professional email."
          prompt={`I want a professional email like hello@[mydomain].com

Options to help me set up:
1. Google Workspace (paid, ~$6/mo)
2. Zoho Mail (free tier available)
3. Email forwarding to my Gmail (free)

I prefer: [CHOOSE ONE]
My domain registrar is: [YOUR REGISTRAR]`}
        />

        {/* Troubleshooting */}
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Troubleshooting</h3>
        
        <div className="space-y-4 mb-12">
          <div className="p-4 rounded-xl border border-gray-200">
            <h4 className="font-medium text-gray-900 mb-2">Domain not showing up?</h4>
            <p className="text-sm text-gray-600">DNS changes can take up to 48 hours. Clear your browser cache and try incognito mode. Check propagation at dnschecker.org.</p>
          </div>
          
          <div className="p-4 rounded-xl border border-gray-200">
            <h4 className="font-medium text-gray-900 mb-2">SSL/HTTPS not working?</h4>
            <p className="text-sm text-gray-600">Most hosting platforms auto-provision SSL. Wait 24 hours after DNS propagation. If still not working, check your hosting platform's SSL settings.</p>
          </div>
          
          <div className="p-4 rounded-xl border border-gray-200">
            <h4 className="font-medium text-gray-900 mb-2">www vs non-www?</h4>
            <p className="text-sm text-gray-600">Pick one as your primary (most choose non-www: yourdomain.com). Set up a redirect from the other. Your hosting platform usually handles this.</p>
          </div>
        </div>

        <AuthorCard author={resource.author} />
      </div>
    </div>
  );
};

export default BackendSetupPage;
